import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ManageApplicationNavComponent } from '../manage-application-nav/manage-application-nav.component';
import { CoreService } from '../../../services/core.service';

import { ListPaginatorComponent } from '../../../components/parts/list-paginator/list-paginator.component';
import { A11yModule } from '@angular/cdk/a11y';

import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

interface LoadParams {
  page: number;
  per_page: number;
  q: string;
}

@Component({
  selector: 'app-manage-application-index-posts',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    ManageApplicationNavComponent,
    ListPaginatorComponent,
    A11yModule
  ],
  templateUrl: './index-posts.component.html',
  styleUrl: './index-posts.component.scss'
})
export class ManageApplicationIndexPostsComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = [
    'new_post',
    'id',
    'domain_name',
    'rss_title',
    'platform_tag',
    'company',
    'scheduled_for',
    'post_status',
    'status_icon',
    'source',
    'enable_repeat'
  ];

  dataSource = new MatTableDataSource<any>([]);
  totalItems = 0;
  pageSize = 12;
  currentPage = 1;
  searchValue = '';
  isLoading = false;
  loading = false;

  private destroy$ = new Subject<void>();

  // single source of truth for pagination + search
  private loadParams$ = new BehaviorSubject<LoadParams>({
    page: 1,
    per_page: 12,
    q: ''
  });

  constructor(
    private router: Router,
    private coreService: CoreService
  ) {}

  ngOnInit() {
    this.loading = false;

    this.loadParams$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (a, b) =>
            a.page === b.page &&
            a.per_page === b.per_page &&
            a.q === b.q
        ),
        switchMap(params => {
          this.isLoading = true;
          return this.coreService.postData('posts/index/all', params);
        })
      )
      .subscribe({
        next: (res: any) => {
          this.dataSource.data = res.data || [];
          this.totalItems = res.total ?? 0;
          this.pageSize = Number(res.per_page) || this.pageSize;
          this.currentPage = res.current_page || 1;
          this.isLoading = false;
        },
        error: err => {
          console.error('Load manage application posts error:', err);
          this.dataSource.data = [];
          this.totalItems = 0;
          this.currentPage = 1;
          this.isLoading = false;
        }
      });

    // initial load
    this.triggerLoad(1, this.pageSize, this.searchValue);
  }

  private triggerLoad(page: number, per_page: number, q: string) {
    this.loadParams$.next({ page, per_page, q });
  }

  onPageChange(event: PageEvent) {
    const page = event.pageIndex + 1;      // MatPaginator is 0-based
    const perPage = event.pageSize;
    this.triggerLoad(page, perPage, this.searchValue);
  }

  onSearchSubmit(value: string) {
    this.searchValue = value.trim();
    this.triggerLoad(1, this.pageSize, this.searchValue);
  }

  clearSearch() {
    this.searchValue = '';
    this.triggerLoad(1, this.pageSize, '');
  }

  createNewPost() {
    this.router.navigateByUrl('/create-post');
  }

  // These navigation helpers are still here in case you re-add actions later.
  editRow(row: any) {
    this.router.navigate(['/edit-post', row.id]);
  }

  reuseRow(row: any) {
    this.router.navigate([
      '/reuse-post',
      row.enable_repeat === -1 ? row.parent_post_id : row.id
    ]);
  }

  repeatPostRow(row: any) {
    this.router.navigate(['/repeat-post', row.id]);
  }

  viewRow(row: any) {
    this.router.navigate([
      '/preview-post',
      row.tag,
      row.enable_repeat === -1 ? row.parent_post_id : row.id
    ]);
  }

  openDeleteConfirm(row: any) {
    // TODO: admin delete confirmation if needed
  }

  domainSlug(value: string | null | undefined): string {
    if (!value) return '';
    return value
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
      .trim();
  }

  onMainImgError(event: Event) {
    const image = event.target as HTMLImageElement;
    image.style.display = 'none';
  }

  isScheduledForToday(value: string | Date | null | undefined): boolean {
    if (!value) return false;

    const scheduledDate = new Date(value);
    const today = new Date();

    return (
      scheduledDate.getDate() === today.getDate() &&
      scheduledDate.getMonth() === today.getMonth() &&
      scheduledDate.getFullYear() === today.getFullYear()
    );
  }

  formatPlatformTag(
    platform: string | null | undefined,
    tag: string | null | undefined
  ): string {
    if (!platform || !tag) {
      return platform || tag || '-';
    }

    const mappings: Record<string, string> = {
      Facebook_photolink: 'Facebook Link Post',
      Facebook_fbphotopost: 'Facebook Photo Post',
      Facebook_fbfirstcomment: 'Facebook Photo Post (first comment)',
      Facebook_fbvideo: 'Facebook Video Post',
      Facebook_fbstoryvideo: 'Facebook Story Video',
      Facebook_fbstoryphoto: 'Facebook Story Photo',
      Facebook_fbreel: 'Facebook Reel Post',
      Facebook_fbstoryreel: 'Facebook Story Reel',

      Instagram_igphotopost: 'Instagram Photo Post',
      Instagram_igvideo: 'Instagram Video Post',
      Instagram_igcarousel: 'Instagram Carousel Post',
      Instagram_igstoryvideo: 'Instagram Story Video',
      Instagram_igstoryphoto: 'Instagram Story Photo',
      Instagram_igreel: 'Instagram Reel Post',
      Instagram_igstoryreel: 'Instagram Story Reel',

      TikTok_tiktokstoryvideo: 'TikTok Story Video',
      TikTok_tiktokstoryphoto: 'TikTok Story Photo',
      TikTok_tiktokcarousel: 'TikTok Carousel Post',
      TikTok_tiktokvideo: 'TikTok Video Post',
      TikTok_tiktokreel: 'TikTok Reel Post',
      TikTok_tiktokstoryreel: 'TikTok Story Reel'
    };

    const key = `${platform}_${tag}`;
    return mappings[key] || `${platform} ${tag}`;
  }

  postStatusForm(status: string | null | undefined): string {
    if (!status) return '-';
    return status.replace(/([A-Z])/g, ' $1').trim();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
